'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = _interopDefault(require('react'));

var useMemo = React.useMemo; // Note, branching disobeys one of the hook rules because
// it wraps hooks in a condition. For this reason, the branch
// is cached and kept the same regardless of updates.

/**
 *
 * @param {Function} condition
 * @param {Function} left
 * @param {Function} right
 * @returns {Function}
 */

var branch = function branch(condition, left) {
  var right = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (x) {
    return x;
  };
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var conditionResult = useMemo(function () {
      return condition(props);
    }, []);
    return conditionResult ? left(props) : right(props);
  };
};

/**
 * @param {Function} component
 * @returns {Object}
 */

var catchRender = function catchRender(component) {
  var newComponent = function newComponent() {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var result = null;

    try {
      result = component(props);
    } catch (e) {
      if (typeof e !== 'object' || React.isValidElement(e)) result = e;else throw e;
    }

    return result;
  };

  newComponent.displayName = component.displayName || component.name || 'Component';
  return newComponent;
};

/**
 * @param {Object} defaultProps
 * @returns {Object}
 */
var defaultProps = function defaultProps(_defaultProps) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var defaults = typeof _defaultProps === 'function' ? _defaultProps(props) : _defaultProps;
    var result = Object.assign({}, props);
    Object.keys(defaults).forEach(function (key) {
      if (result[key] === undefined) {
        result[key] = defaults[key];
      }
    });
    return result;
  };
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

/**
 * @param {string|symbol} propName
 * @returns {Object}
 */
var flattenProp = function flattenProp(propName) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread({}, props, props[propName]);
  };
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

var useEffect = React.useEffect,
    useRef = React.useRef,
    useState = React.useState;

function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
}
/**
 * @param {Object} spec
 * @returns {Object}
 */


var lifecycle = function lifecycle(spec) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _useState = useState({}),
        _useState2 = _slicedToArray(_useState, 2),
        state = _useState2[0],
        setStateRaw = _useState2[1];

    var setState = function setState(update) {
      setStateRaw(_objectSpread({}, state, typeof update === 'function' ? update(state) : update));
    };

    var self = {
      props: props,
      state: state,
      setState: setState
    };

    if (spec.componentDidMount) {
      useEffect(function () {
        spec.componentDidMount.call(self);
      }, []);
    }

    if (spec.componentWillUnmount) {
      useEffect(function () {
        return function () {
          spec.componentWillUnmount.call(self);
        };
      }, []);
    }

    if (spec.componentDidUpdate) {
      var previousProps = usePrevious(props);
      useEffect(function () {
        spec.componentDidUpdate.call(self, previousProps, null, null);
      });
    }

    return _objectSpread({}, props, state);
  };
};

/**
 * @param {Function} fn
 * @returns {Object}
 */
var mapProps = function mapProps(fn) {
  return fn;
};

/**
 * @param {string|symbol} propName
 * @param {Function} enhance
 * @returns {Object}
 */
var namespace = function namespace(propName, enhance) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread({}, props, _defineProperty({}, propName, enhance(props)()));
  };
};

/**
 * @param  {...Function[]} fns
 * @returns {Object}
 */
var pipe = function pipe() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return fns.reduce(function (v, f) {
      return f(v);
    }, props);
  };
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

/**
 * @param {string|symbol} a
 * @param {string|symbol} b
 * @returns {Object}
 */
var renameProp = function renameProp(a, b) {
  return function () {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var prop = _ref[a],
        props = _objectWithoutProperties(_ref, [a]);

    return _objectSpread({}, props, _defineProperty({}, b, prop));
  };
};

/**
 * @param {Object} propMap
 * @returns {Object}
 */
var renameProps = function renameProps(propMap) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread({}, Object.entries(props).filter(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 1),
          key = _ref2[0];

      return !(key in propMap);
    }).reduce(function (obj, _ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
          k = _ref4[0],
          v = _ref4[1];

      return Object.assign(obj, _defineProperty({}, k, v));
    }, {}), Object.entries(propMap).map(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
          oldName = _ref6[0],
          newName = _ref6[1];

      return [newName, props[oldName]];
    }).reduce(function (obj, _ref7) {
      var _ref8 = _slicedToArray(_ref7, 2),
          k = _ref8[0],
          v = _ref8[1];

      return Object.assign(obj, _defineProperty({}, k, v));
    }, {}));
  };
};

/**
 * @param {any} comp
 * @returns {Object}
 */
var renderComponent = function renderComponent(comp) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    throw comp(props);
  };
};

/**
 * @returns {Object}
 */
var renderNothing = function renderNothing()
/* props */
{
  // eslint-disable-next-line
  throw null;
};

var useMemo$1 = React.useMemo;
/**
 *
 * @param {Object} handlers
 * @returns {Object}
 */

var withHandlers = function withHandlers(handlers) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var realHandlers = useMemo$1(function () {
      return typeof handlers === 'function' ? handlers(props) : handlers;
    }, []);
    var actionTypes = Object.keys(realHandlers);
    var boundHandlers = actionTypes.reduce(function (obj, type) {
      return Object.assign(obj, _defineProperty({}, type, function () {
        return realHandlers[type](props).apply(void 0, arguments);
      }));
    }, {});
    return _objectSpread({}, props, boundHandlers);
  };
};

var useMemo$2 = React.useMemo,
    useRef$1 = React.useRef,
    useEffect$1 = React.useEffect;

function usePrevious$1(value) {
  var ref = useRef$1();
  useEffect$1(function () {
    ref.current = value;
  });
  return ref.current;
}
/**
 *
 * @param {any} shouldMapOrKeys
 * @param {Function} createProps
 * @returns {Object}
 */


var withPropsOnChange = function withPropsOnChange(shouldMapOrKeys, createProps) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var previousProps = usePrevious$1(props);
    var keys = Array.isArray(shouldMapOrKeys) ? shouldMapOrKeys.map(function (key) {
      return props[key];
    }) : shouldMapOrKeys(props, previousProps) ? undefined : [];
    var mappedProps = useMemo$2(function () {
      return createProps(props);
    }, keys);
    return _objectSpread({}, props, mappedProps);
  };
};

/**
 *
 * @param {Function|Object} fn
 */
var withProps = function withProps(fn) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread({}, props, typeof fn === 'function' ? fn(props) : fn);
  };
};

var useReducer = React.useReducer,
    useMemo$3 = React.useMemo;
/**
 *
 * @param {string|symbol} stateName
 * @param {string|symbol} dispatchName
 * @param {Function} reducer
 * @param {any} initialValue
 */

var withReducer = function withReducer(stateName, dispatchName, reducer, initialValue) {
  return function (props) {
    var _objectSpread2;

    var _useReducer = useReducer(reducer, typeof initialValue === 'function' ? useMemo$3(function () {
      return initialValue(props);
    }, []) : initialValue),
        _useReducer2 = _slicedToArray(_useReducer, 2),
        state = _useReducer2[0],
        dispatch = _useReducer2[1];

    return _objectSpread({}, props, (_objectSpread2 = {}, _defineProperty(_objectSpread2, stateName, state), _defineProperty(_objectSpread2, dispatchName, dispatch), _objectSpread2));
  };
};

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }

    return arr2;
  }
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

var useReducer$1 = React.useReducer,
    useMemo$4 = React.useMemo;
/**
 *
 * @param {any} initialValue
 * @param {Object} handlers
 * @returns {Object}
 */

var withStateHandlers = function withStateHandlers(initialValue, handlers) {
  return function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var actionTypes = Object.keys(handlers);

    var reducer = function reducer(state, action) {
      return _objectSpread({}, state, handlers[action.type](state, props).apply(void 0, _toConsumableArray(action.payload)));
    };

    var _useReducer = useReducer$1(reducer, typeof initialValue === 'function' ? useMemo$4(function () {
      return initialValue(props);
    }, []) : initialValue),
        _useReducer2 = _slicedToArray(_useReducer, 2),
        state = _useReducer2[0],
        dispatch = _useReducer2[1];

    var boundHandlers = actionTypes.reduce(function (obj, type) {
      return Object.assign(obj, _defineProperty({}, type, function () {
        for (var _len = arguments.length, payload = new Array(_len), _key = 0; _key < _len; _key++) {
          payload[_key] = arguments[_key];
        }

        if (payload !== undefined) dispatch({
          type: type,
          payload: payload
        });
      }));
    }, {});
    return _objectSpread({}, props, state, boundHandlers);
  };
};

var useState$1 = React.useState,
    useMemo$5 = React.useMemo;
/**
 *
 * @param {string|symbol} stateName
 * @param {string|symbol} stateUpdaterName
 * @param {any} initialState
 */

var withState = function withState(stateName, stateUpdaterName, initialState) {
  return function () {
    var _objectSpread2;

    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var _useState = useState$1(typeof initialState === 'function' ? useMemo$5(function () {
      return initialState(props);
    }, []) : initialState),
        _useState2 = _slicedToArray(_useState, 2),
        state = _useState2[0],
        update = _useState2[1];

    return _objectSpread({}, props, (_objectSpread2 = {}, _defineProperty(_objectSpread2, stateName, state), _defineProperty(_objectSpread2, stateUpdaterName, update), _objectSpread2));
  };
};

/**
 * @param {Function} fn
 * @returns {Object}
 */
var tap = function tap(fn) {
  return function (props) {
    fn(props);
    return props;
  };
};

var omitProps = function omitProps(keys) {
  return function (props) {
    keys = [].concat(keys);
    var result = {};

    for (var key in props) {
      if (keys.indexOf(key) < 0) {
        result[key] = props[key];
      }
    }

    return result;
  };
};

/**
 *
 * @param {Function|Object} fn
 */
var withProp = function withProp(key, fn) {
  return function (props) {
    return _objectSpread({}, props, _defineProperty({}, key, typeof fn === 'function' ? fn(props) : fn));
  };
};

// @ts-check

exports.branch = branch;
exports.catchRender = catchRender;
exports.defaultProps = defaultProps;
exports.flattenProp = flattenProp;
exports.lifecycle = lifecycle;
exports.mapProps = mapProps;
exports.namespace = namespace;
exports.pipe = pipe;
exports.renameProp = renameProp;
exports.renameProps = renameProps;
exports.renderComponent = renderComponent;
exports.renderNothing = renderNothing;
exports.withHandlers = withHandlers;
exports.withPropsOnChange = withPropsOnChange;
exports.withProps = withProps;
exports.withReducer = withReducer;
exports.withStateHandlers = withStateHandlers;
exports.withState = withState;
exports.tap = tap;
exports.omitProps = omitProps;
exports.withProp = withProp;
